import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import NotificationBell from '../NotificationBell';

function TopBar() {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchTerm.trim();
    navigate(q ? `/?q=${encodeURIComponent(q)}` : '/');
  };

  const username = currentUser?.Username || currentUser?.username || '';
  const displayName = currentUser?.DisplayName || currentUser?.display_name || currentUser?.displayName || username;
  const avatar = currentUser?.Avatar || currentUser?.avatar || currentUser?.ProfileImage || currentUser?.profile_image;
  const fallbackAvatar = displayName
    ? `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&size=36&background=E6F7FA&color=4CBED9&bold=true`
    : null;

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-[12px] border-b border-surface-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center shadow-soft-sm">
              <span className="text-white font-bold text-lg leading-none">C</span>
            </div>
            <span className="text-h3 text-surface-900 hidden sm:block">
              Cli<span className="text-primary-500">QQ</span>
            </span>
          </Link>

          {/* Center: Search bar */}
          <form onSubmit={handleSearch} role="search" className="hidden md:flex flex-1 max-w-md mx-6">
            <div className="relative w-full">
              <button
                type="submit"
                aria-label="Search"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-primary-500 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search events by name, city, or description..."
                className="w-full bg-surface-100 border-0 rounded-xl pl-10 pr-4 py-2.5 text-body-sm text-surface-900 placeholder:text-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:bg-white transition-all duration-200"
              />
            </div>
          </form>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 sm:gap-3">
            {currentUser ? (
              <>
                {/* Create button */}
                <Link
                  to="/create-cliqq"
                  className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 transition-colors duration-200 shadow-soft-sm"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Create
                </Link>

                {/* Notification bell */}
                <NotificationBell />

                {/* User avatar */}
                <Link
                  to="/my-page"
                  className="flex-shrink-0 rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2"
                  aria-label="My profile"
                >
                  <img
                    src={avatar || fallbackAvatar}
                    alt={displayName}
                    className="w-9 h-9 rounded-xl object-cover bg-surface-50"
                    onError={(e) => {
                      e.target.onerror = null;
                      if (fallbackAvatar) {
                        e.target.src = fallbackAvatar;
                      }
                    }}
                  />
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-body-sm font-semibold text-surface-600 hover:text-surface-900 transition-colors duration-200 px-3 py-2"
                >
                  Log in
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center px-4 py-2 bg-primary-500 text-white text-body-sm font-semibold rounded-xl hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:ring-offset-2 transition-colors duration-200"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default TopBar;
